import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success: boolean; message: string } | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const key = event.target.name === 'user_name' ? 'name' : event.target.name === 'user_email' ? 'email' : 'message';
    setFormData(previous => ({ ...previous, [key]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formRef.current) return;

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setSubmitStatus({ success: false, message: 'Contact form is currently unavailable. Please email me directly.' });
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitStatus(null);
      await emailjs.sendForm(serviceId, templateId, formRef.current, publicKey);
      setSubmitStatus({ success: true, message: 'Message sent. I will get back to you soon.' });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({ success: false, message: 'Message could not be sent. Please email me directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact section" id="contact">
      <div className="container">
        <div className="section-head">
          <p className="section-head__kicker">Contact</p>
          <h2 className="section-head__title">Have a project in mind?</h2>
          <p className="section-head__lede">Send me a message and tell me what you are working on.</p>
        </div>

        <div className="contact__container">
          <div className="contact__info">
            <div className="contact__card">
              <i className="ri-mail-line contact__card-icon" aria-hidden="true" />
              <div>
                <h3 className="contact__card-title">Email</h3>
                <span className="contact__card-data">Yegorcherov@gmail.com</span>
                <a href="mailto:Yegorcherov@gmail.com" className="contact__card-button">Write me <i className="ri-arrow-right-line" aria-hidden="true" /></a>
              </div>
            </div>
            <div className="contact__card">
              <i className="ri-linkedin-box-line contact__card-icon" aria-hidden="true" />
              <div>
                <h3 className="contact__card-title">LinkedIn</h3>
                <span className="contact__card-data">Yegor Cherov</span>
                <a href="https://www.linkedin.com/in/yegor-cherov" target="_blank" rel="noopener noreferrer" className="contact__card-button">Connect <i className="ri-arrow-right-line" aria-hidden="true" /></a>
              </div>
            </div>
            <div className="contact__card">
              <i className="ri-github-line contact__card-icon" aria-hidden="true" />
              <div>
                <h3 className="contact__card-title">GitHub</h3>
                <span className="contact__card-data">YegorCherov</span>
                <a href="https://github.com/YegorCherov" target="_blank" rel="noopener noreferrer" className="contact__card-button">View Profile <i className="ri-arrow-right-line" aria-hidden="true" /></a>
              </div>
            </div>
          </div>

          <form ref={formRef} className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__form-div">
              <label htmlFor="contact-name" className="sr-only">Your name</label>
              <input id="contact-name" type="text" name="user_name" value={formData.name} onChange={handleChange} className="contact__form-input" placeholder="Your name" required />
            </div>
            <div className="contact__form-div">
              <label htmlFor="contact-email" className="sr-only">Your email</label>
              <input id="contact-email" type="email" name="user_email" value={formData.email} onChange={handleChange} className="contact__form-input" placeholder="Your email" required />
            </div>
            <div className="contact__form-div">
              <label htmlFor="contact-message" className="sr-only">Your message</label>
              <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} className="contact__form-input contact__form-area" placeholder="Your message" required />
            </div>
            <button type="submit" className="btn contact__form-button" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
            {submitStatus && <div className={`contact__form-status ${submitStatus.success ? 'success' : 'error'}`} role="status">{submitStatus.message}</div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;